import type { NavbarContext, NavbarMenuItem, AuthButtonContext } from './types'

// ── Structure ────────────────────────────────────────────────────────────────
//
// Defines which items exist and in what order.
// `handle` = URL path segment (may be Polish, stays as-is).
// `labelKey` = English key into the locale labels dict (defaults to handle).
// Labels come from locales/{lang}/navbar.json (flat key→string map).
// Hrefs default to /${handle}; override dynamic ones in navbarHrefOverrides.

interface NavbarSubItemDef {
  handle: string
  labelKey?: string
}

interface NavbarItemDef {
  handle: string
  labelKey?: string
  submenu?: NavbarSubItemDef[]
}

const NAV_STRUCTURE: NavbarItemDef[] = [
  { handle: 'driving-schools', labelKey: 'drivingSchools' },
  { handle: 'osrodki-word', labelKey: 'wordCenters' },
  {
    handle: 'knowledge-base',
    labelKey: 'knowledgeBase',
    submenu: [
      { handle: 'strefa-nauki', labelKey: 'learningZone' },
      { handle: 'blog' },
      { handle: 'znaki-drogowe', labelKey: 'roadSigns' },
      { handle: 'kodeks-drogowy', labelKey: 'trafficCode' },
      { handle: 'predkosciomierz', labelKey: 'speedometer' },
    ],
  },
  { handle: 'dla-osk', labelKey: 'forDrivingSchools' },
  { handle: 'kontakt', labelKey: 'contact' },
]

// ── Dynamic href overrides ───────────────────────────────────────────────────
//
// Add an entry here whenever a link should resolve differently based on
// runtime state (auth, feature flags, etc.).
// Items not listed fall back to the default /${handle} href.

export const navbarHrefOverrides: Partial<
  Record<string, (ctx: NavbarContext) => string>
> = {
  'strefa-nauki': (ctx) => ctx.testsAndGamesHref,
}

// ── Builder ──────────────────────────────────────────────────────────────────

/**
 * Builds the full navbar menu list from locale labels and runtime context.
 * @param labels - flat {handle: label} map from locales/{lang}/navbar.json
 * @param ctx    - runtime context for dynamic href resolution
 */
export function buildNavbarMenuList(
  labels: Record<string, string>,
  ctx: NavbarContext,
): NavbarMenuItem[] {
  return NAV_STRUCTURE.map((item) => ({
    text: labels[item.labelKey ?? item.handle] ?? item.handle,
    href: `/${item.handle}`,
    handle: item.handle,
    submenu: item.submenu?.map((sub) => ({
      text: labels[sub.labelKey ?? sub.handle] ?? sub.handle,
      handle: sub.handle,
      href: navbarHrefOverrides[sub.handle]?.(ctx) ?? `/${sub.handle}`,
    })),
  }))
}

// ── Auth button ──────────────────────────────────────────────────────────────

/**
 * Resolves the href for the navbar auth/panel button.
 * Returns the user's highest-priority panel when signed in,
 * otherwise points to the login page.
 */
export function getAuthButtonHref(ctx: AuthButtonContext): string {
  if (!ctx.isLoaded || !ctx.isSignedIn) return '/logowanie'
  return ctx.highestPriorityPanelUrl
}

/**
 * Resolves which locale key to use for the auth/panel button label.
 * The returned key maps directly to a top-level key in navbar.json.
 */
export function getAuthButtonLabelKey(
  ctx: Pick<AuthButtonContext, 'isSignedIn' | 'isLoaded'>,
): 'login' | 'panel' {
  return ctx.isLoaded && ctx.isSignedIn ? 'panel' : 'login'
}
